<script setup lang="ts">
  import { getCurrentInstance, onMounted, ref, watch } from 'vue';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import ChangelogModal from '@/components/modals/ChangelogModal.vue';
  import { useModal } from '@/composition/use-modal';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    loadChangelogMarkdown,
    newsButtonLabel,
  } from '@/domain/changelog/changelog';

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  const modals = useModal();

  const restaurantStore = useRestaurantStore();

  // Button label derives from the latest changelog entry headline, so it can
  // never go stale; falls back to a generic label if loading/parsing fails.
  const changelogButtonLabel = ref(String(proxy.$t('dashboard.whatsNewButton')));

  const refreshLabel = async () => {
    changelogButtonLabel.value = String(proxy.$t('dashboard.whatsNewButton'));
    const locale = proxy.$i18n.locale;
    try {
      const md = await loadChangelogMarkdown(locale);
      // Discard stale results: a quick locale double-switch could otherwise
      // resolve out of order and overwrite the label with the older language.
      if (proxy.$i18n.locale !== locale) return;
      const label = newsButtonLabel(md);
      if (label) changelogButtonLabel.value = label;
    } catch (error) {
      console.warn('Failed to load changelog headline:', error);
    }
  };

  onMounted(refreshLabel);
  watch(() => proxy.$i18n.locale, refreshLabel);

  // Methods
  const openChangelog = () => {
    modals.show(
      ChangelogModal,
      {
        restaurant: restaurantStore.restaurant,
      },
      {
        name: 'changelog',
        classes: 'dialog--height-limit',
        adaptive: true,
        height: 'auto',
        width: 700,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      },
      {}
    );
  };
</script>

<template>
  <v-card class="messages-card" elevation="0">
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-bell</v-icon>
          <span class="header-title">{{ $t('dashboard.news') }}</span>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="card-content">
      <!-- Latest Update -->
      <v-btn color="success" @click="openChangelog" class="changelog-btn" block>
        {{ changelogButtonLabel }}
      </v-btn>

      <!-- Colombia Claro Alert -->
      <v-alert
        v-if="
          restaurantStore.restaurant?.country === 'CO' &&
          new Date(restaurantStore.restaurant?.createdAt) <
            new Date('2025-02-01')
        "
        type="warning"
        text
        dense
        class="system-alert"
      >
        <strong>{{ $t('dashboard.claroAlertTitle') }}</strong
        ><br />
        {{ $t('dashboard.claroAlertText') }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .messages-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;

    .card-header {
      @apply tw-pb-2 tw-pt-3;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-0;

      .changelog-btn {
        @apply tw-mb-3;
        @apply tw-justify-start tw-text-left;
      }

      .system-alert {
        @apply tw-mb-2 last:tw-mb-0;
      }
    }
  }
</style>
